import { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Heart, MessageCircle, X } from 'lucide-react';
import { SocialPostImage } from './SocialPostImage';
import { AvatarDisplay } from './AvatarDisplay';
import type { SocialPost, SocialComment } from './socialTypes';

const POST_DETAIL_MODAL_Z = 10050;

export interface PostDetailModalProps {
  post: SocialPost;
  onClose: () => void;
  onLike: (postId: string) => void;
  onAddComment: (postId: string, text: string) => void;
}

/**
 * Instagram-style post detail card: small modal so User A can view, like, and comment
 * on another user's post (e.g. from their profile).
 * Comment Like is local-only (no backend); Reply posts as "@AuthorName text".
 */
export function PostDetailModal({
  post,
  onClose,
  onLike,
  onAddComment,
}: PostDetailModalProps) {
  const { t } = useTranslation();
  const [commentText, setCommentText] = useState('');
  const [commentLikesSet, setCommentLikesSet] = useState<Set<string>>(
    () => new Set()
  );
  const [replyingTo, setReplyingTo] = useState<SocialComment | null>(null);

  const handleSubmitComment = useCallback(() => {
    const trimmed = commentText.trim();
    if (!trimmed) return;
    const textToSend = replyingTo
      ? `@${replyingTo.author} ${trimmed}`
      : trimmed;
    onAddComment(post.id, textToSend);
    setCommentText('');
    setReplyingTo(null);
  }, [commentText, replyingTo, post.id, onAddComment]);

  const toggleCommentLike = useCallback((commentId: string) => {
    setCommentLikesSet((prev) => {
      const next = new Set(prev);
      if (next.has(commentId)) next.delete(commentId);
      else next.add(commentId);
      return next;
    });
  }, []);

  const modalContent = (
    <div
      className="fixed inset-0 flex items-center justify-center p-4 bg-black/60"
      style={{ zIndex: POST_DETAIL_MODAL_Z }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={t('social.profile.postDetailView')}
    >
      {/* Wrapper with fixed width so the card cannot be stretched to full page by flex or global CSS */}
      <div
        style={{
          width: '320px',
          maxWidth: 'calc(100vw - 2rem)',
          flexShrink: 0,
          flexGrow: 0,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Small card: square photo on top, then scrollable likes + comments */}
        <div
          className="relative bg-white dark:bg-[#16151D] rounded-2xl border border-[#E2DFEA] dark:border-white/8 shadow-xl flex flex-col overflow-hidden"
          style={{
            width: '100%',
            maxHeight: '85vh',
            boxSizing: 'border-box',
          }}
        >
          {/* Close button */}
          <button
            type="button"
            onClick={onClose}
            className="absolute top-2 right-2 z-10 p-1.5 rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors"
            aria-label={t('common.close')}
          >
            <X className="w-4 h-4" />
          </button>

          {/* Square image – fixed aspect 1:1 so full photo fits in small popup */}
          <div
            className="relative flex-shrink-0 bg-[#F0EFF5] dark:bg-white/10"
            style={{ width: '100%', aspectRatio: '1', minHeight: 0 }}
          >
            <SocialPostImage
              src={post.images?.[0]}
              alt={post.ownerName}
              className="w-full h-full object-contain"
            />
          </div>

          {/* Scrollable section: header, caption, likes, comments, add-comment */}
          <div className="flex flex-col flex-1 min-h-0 overflow-y-auto overscroll-contain p-3">
            {/* Owner row */}
            <div className="flex items-center gap-2 mb-2 flex-shrink-0">
              <AvatarDisplay
                src={post.ownerAvatar}
                alt={post.ownerName}
                fallback="👤"
                className="text-lg"
                containerClassName="w-7 h-7 flex-shrink-0"
              />
              <span
                className="font-semibold text-[#2D2F7E] dark:text-white truncate text-sm"
                style={{ fontFamily: 'Sora, sans-serif' }}
              >
                {post.ownerName}
              </span>
            </div>

            {/* Caption */}
            {post.caption ? (
              <p
                className="text-xs text-[#464452] dark:text-white/80 mb-2 line-clamp-2 break-words"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {post.caption}
              </p>
            ) : null}

            {/* Actions: like + comment count */}
            <div className="flex items-center gap-3 py-1.5 border-t border-b border-[#E2DFEA] dark:border-white/8 mb-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => onLike(post.id)}
                className="flex items-center gap-1 group"
              >
                <Heart
                  className={`w-5 h-5 transition-colors ${
                    post.isLiked
                      ? 'text-[#936FEF] fill-[#936FEF]'
                      : 'text-[#464452] dark:text-white/70 group-hover:text-[#936FEF]'
                  }`}
                />
                <span
                  className="text-xs font-semibold text-[#2D2F7E] dark:text-white"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {post.likes}
                </span>
              </button>
              <div className="flex items-center gap-1 text-[#464452] dark:text-white/70">
                <MessageCircle className="w-5 h-5" />
                <span
                  className="text-xs font-semibold"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {post.comments?.length ?? 0} {t('social.comments')}
                </span>
              </div>
            </div>

            {/* Comments list – scrolls with the section */}
            <div className="space-y-2 mb-2">
              {(post.comments?.length ?? 0) === 0 ? (
                <p
                  className="text-xs text-[#464452] dark:text-white/50 py-1"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('social.noCommentsYet')}
                </p>
              ) : (
                post.comments?.map((comment) => {
                  const isCommentLiked = commentLikesSet.has(comment.id);
                  return (
                    <div key={comment.id} className="flex gap-2">
                      <AvatarDisplay
                        src={comment.avatar}
                        alt={comment.author || 'User'}
                        fallback="👤"
                        className="text-xs"
                        containerClassName="w-7 h-7 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-1.5 flex-wrap">
                          <span
                            className="font-bold text-[#2D2F7E] dark:text-white text-xs"
                            style={{ fontFamily: 'Sora, sans-serif' }}
                          >
                            {comment.author || 'User'}
                          </span>
                          <span
                            className="text-[10px] text-[#464452]/50 dark:text-white/40"
                            style={{ fontFamily: 'Inter, sans-serif' }}
                          >
                            {comment.timestamp}
                          </span>
                        </div>
                        <p
                          className="text-xs text-[#464452] dark:text-white/80 break-words mt-0.5"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          {comment.text}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <button
                            type="button"
                            onClick={() => toggleCommentLike(comment.id)}
                            className={`text-[10px] font-semibold transition-colors ${
                              isCommentLiked
                                ? 'text-[#936FEF]'
                                : 'text-[#464452] dark:text-white/50 hover:text-[#936FEF]'
                            }`}
                            style={{ fontFamily: 'Inter, sans-serif' }}
                          >
                            {isCommentLiked
                              ? t('social.liked')
                              : t('social.like')}
                          </button>
                          <button
                            type="button"
                            onClick={() => setReplyingTo(comment)}
                            className="text-[10px] font-semibold text-[#464452] dark:text-white/50 hover:text-[#936FEF] transition-colors"
                            style={{ fontFamily: 'Inter, sans-serif' }}
                          >
                            {t('social.reply')}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Add comment – at bottom of scroll area */}
            <div className="flex flex-col gap-1 flex-shrink-0 pt-1">
              {replyingTo && (
                <div className="flex items-center gap-2">
                  <span
                    className="text-[10px] text-[#464452] dark:text-white/70"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('social.replyingTo', { name: replyingTo.author })}
                  </span>
                  <button
                    type="button"
                    onClick={() => setReplyingTo(null)}
                    className="text-[10px] font-semibold text-[#936FEF] hover:underline"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              )}
              <div className="flex gap-1.5">
                <input
                  type="text"
                  placeholder={
                    replyingTo
                      ? t('social.replyToCommentPlaceholder', {
                          name: replyingTo.author,
                        })
                      : t('social.addComment')
                  }
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSubmitComment();
                    }
                  }}
                  className="flex-1 px-2.5 py-1.5 rounded-lg text-xs outline-none border border-[#E2DFEA] dark:border-white/20 bg-[#F0EFF5] dark:bg-white/5 text-[#464452] dark:text-white placeholder:text-[#464452]/50 dark:placeholder:text-white/50"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                />
                <button
                  type="button"
                  onClick={handleSubmitComment}
                  disabled={!commentText.trim()}
                  className="px-3 py-1.5 rounded-lg text-white text-xs font-semibold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
                  style={{
                    background:
                      'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                    fontFamily: 'Sora, sans-serif',
                  }}
                >
                  {t('social.post')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
